/* =============================================================================
   DiSEGNO Builder — Saved Design store
   -----------------------------------------------------------------------------
   Two implementations behind one interface:

     LocalDraftStore   — always on. Persists the in-progress design (keyed on
                         design_id) to localStorage so a reload / accidental
                         close resumes the same design_id + configuration.
                         NOT a "saved design" in the customer sense — no email,
                         no recovery link, device-only.

     LinkStore         — "Save your DiSEGNO" with no backend. The design (jewelry
                         type, starter, ordered element SKUs) is encoded into
                         the recovery link itself: <builder page>?design=v1.<code>.
                         Klaviyo receives that link on "DiSEGNO Design Saved";
                         opening it rebuilds the design. A design is not
                         personal data, so nothing sensitive is in the URL.
                         Limits: no cross-device "my designs" list, and a sent
                         link can't be edited later.

     AppProxyStore     — the full server-backed version (not built yet). Talks to a Shopify
                         App Proxy endpoint (a companion app you install). The
                         app stores the design against a customer + email and in
                         customer metafields / a metaobject, and returns an
                         OPAQUE recovery token. Klaviyo emails link to
                         /apps/disegno/d/<token>. The token is unguessable and
                         resolves server-side — no customer PII in the URL.

   Interface (both return Promises):
     save(designRecord)            -> { design_id, recovery_token?, recovery_url?, saved_at }
     load(idOrToken)               -> designRecord | null
     listForSession()              -> [designRecord]           (local only, best effort)
     attachEmail(design_id, email) -> { ok, recovery_url? }    (AppProxy only)

   designRecord (the canonical saved shape — spec §16):
     {
       design_id, builder_session_id,
       jewelry_type,                          'bracelet'|'necklace'|'earrings'
       design_source,                         'custom'|'curated'|'remix'
       curated_design_id?,
       ear_mode?,                             'single'|'pair'|'asymmetrical'
       starter_variant_id?,
       elements: [variant_id],                bracelet/necklace ordered run
       left_earring:  [variant_id],
       right_earring: [variant_id],
       design_value, currency,
       saved_at, updated_at,
       identity: { identified:bool, source?, email_provided?:bool }   // NO raw email client-side
     }

   The App Proxy REQUEST/RESPONSE contract is documented in
   shopify/app-proxy-contract.md — the app itself is the remaining backend build.
   ========================================================================== */
(function (root) {
  'use strict';

  var Disegno = (root.Disegno = root.Disegno || {});
  if (Disegno.SavedDesign) return;

  var Identity = Disegno.Identity;
  var Analytics = Disegno.Analytics;

  var config = {
    driver: 'local',                 // 'local' | 'link' | 'appproxy'
    linkParam: 'design',             // ?design=v1.<code> on the builder page
    linkBase: null,                  // absolute builder url; defaults to the current page
    proxyBase: '/apps/disegno',      // Shopify App Proxy subpath
    recoveryPath: '/d/',             // recovery url = proxyBase + recoveryPath + token
    localKeyPrefix: 'disegno.saved.',
    maxLocal: 10
  };

  function configure(opts) {
    if (!opts) return;
    for (var k in opts) if (Object.prototype.hasOwnProperty.call(opts, k)) config[k] = opts[k];
  }

  /* ---- LocalDraftStore -------------------------------------------------- */
  function localSave(rec) {
    rec = stamp(rec);
    try {
      root.localStorage.setItem(config.localKeyPrefix + rec.design_id, JSON.stringify(rec));
      trimLocal();
    } catch (e) {}
    return Promise.resolve({ design_id: rec.design_id, saved_at: rec.saved_at, driver: 'local' });
  }
  function localLoad(id) {
    try {
      var raw = root.localStorage.getItem(config.localKeyPrefix + id);
      return Promise.resolve(raw ? JSON.parse(raw) : null);
    } catch (e) { return Promise.resolve(null); }
  }
  function localList() {
    var out = [];
    try {
      for (var i = 0; i < root.localStorage.length; i++) {
        var key = root.localStorage.key(i);
        if (key && key.indexOf(config.localKeyPrefix) === 0) {
          try { out.push(JSON.parse(root.localStorage.getItem(key))); } catch (e) {}
        }
      }
    } catch (e) {}
    return Promise.resolve(out.sort(function (a, b) { return (b.updated_at || '').localeCompare(a.updated_at || ''); }));
  }
  function trimLocal() {
    try {
      var keys = [];
      for (var i = 0; i < root.localStorage.length; i++) {
        var k = root.localStorage.key(i);
        if (k && k.indexOf(config.localKeyPrefix) === 0) keys.push(k);
      }
      if (keys.length <= config.maxLocal) return;
      keys
        .map(function (k) { var r = JSON.parse(root.localStorage.getItem(k) || '{}'); return { k: k, t: r.updated_at || '' }; })
        .sort(function (a, b) { return a.t.localeCompare(b.t); })
        .slice(0, keys.length - config.maxLocal)
        .forEach(function (o) { root.localStorage.removeItem(o.k); });
    } catch (e) {}
  }

  /* ---- LinkStore ------------------------------------------------------------
     Compact shape (short keys keep the link short):
       { v:1, t:'b'|'n'|'e', o:source, c:curated_id,
         s:1                  loop starter selected
         e:[sku]              loop elements, or earring 1
         f:[sku]              earring 2 (asymmetrical only)
         q:1|2, m:0|1, k:0|1|2  earring qty / matched / starter studs bought }
     -------------------------------------------------------------------------- */
  var LINK_PREFIX = 'v1.';
  var TYPE_CODE = { bracelet: 'b', necklace: 'n', earrings: 'e' };
  var TYPE_NAME = { b: 'bracelet', n: 'necklace', e: 'earrings' };
  var SOURCES = ['custom', 'curated', 'remix', 'quiz'];
  var SKU_RE = /^[A-Za-z0-9._\-]{1,64}$/;
  var MAX_ELS = 80;

  function isLinkCode(s) { return typeof s === 'string' && s.indexOf(LINK_PREFIX) === 0; }

  function encodeLink(rec) {
    var ear = rec.jewelry_type === 'earrings';
    var o = { v: 1, t: TYPE_CODE[rec.jewelry_type] || 'b' };
    if (rec.design_source && rec.design_source !== 'custom') o.o = rec.design_source;
    if (rec.curated_design_id) o.c = String(rec.curated_design_id);
    if (ear) {
      o.e = rec.ear1_skus || [];
      if (rec.earring_quantity === 1) o.q = 1;
      if (rec.earring_quantity !== 1 && rec.match_earrings === false) { o.m = 0; o.f = rec.ear2_skus || []; }
      if (rec.starter_quantity === 0 || rec.starter_quantity === 1 || rec.starter_quantity === 2) o.k = rec.starter_quantity;
    } else {
      o.e = rec.elements_skus || [];
      if (rec.starter_selected) o.s = 1;
    }
    return LINK_PREFIX + b64urlEncode(JSON.stringify(o));
  }

  // Returns a normalised, validated design or null. Everything here came from a
  // URL, so every field is whitelisted / pattern-checked before use.
  function decodeLink(code) {
    if (!isLinkCode(code)) return null;
    var o;
    try { o = JSON.parse(b64urlDecode(code.slice(LINK_PREFIX.length))); } catch (e) { return null; }
    if (!o || o.v !== 1 || !TYPE_NAME[o.t]) return null;
    var skus = function (a) {
      if (!Array.isArray(a) || a.length > MAX_ELS) return null;
      for (var i = 0; i < a.length; i++) if (typeof a[i] !== 'string' || !SKU_RE.test(a[i])) return null;
      return a.slice();
    };
    var jt = TYPE_NAME[o.t];
    var out = {
      jewelry_type: jt,
      design_source: SOURCES.indexOf(o.o) > -1 ? o.o : 'custom',
      curated_design_id: (typeof o.c === 'string' && /^[A-Za-z0-9_\-]{1,80}$/.test(o.c)) ? o.c : null
    };
    var e1 = skus(o.e || []);
    if (!e1) return null;
    if (jt === 'earrings') {
      out.earring_quantity = o.q === 1 ? 1 : 2;
      out.match_earrings = out.earring_quantity === 2 ? o.m !== 0 : null;
      out.ear1_skus = e1;
      out.ear2_skus = out.match_earrings === false ? (skus(o.f || []) || []) : [];
      out.starter_quantity = (o.k === 0 || o.k === 1 || o.k === 2) ? o.k : null;
      if (!e1.length && !out.ear2_skus.length) return null;
    } else {
      out.starter_selected = o.s === 1;
      out.elements_skus = e1;
      if (!e1.length && !out.starter_selected) return null;
    }
    return out;
  }

  function linkUrl(code) {
    var base = config.linkBase;
    if (!base) { try { base = root.location.origin + root.location.pathname; } catch (e) { base = ''; } }
    return base + (base.indexOf('?') > -1 ? '&' : '?') + config.linkParam + '=' + encodeURIComponent(code);
  }

  function linkSave(rec) {
    rec = stamp(rec);
    var url = linkUrl(encodeLink(rec));
    // also keep a device copy so this browser can list / resume it
    try {
      var local = clone(rec); delete local.__email; local.recovery_url = url;
      root.localStorage.setItem(config.localKeyPrefix + rec.design_id, JSON.stringify(local));
      trimLocal();
    } catch (e) {}
    return Promise.resolve({ design_id: rec.design_id, recovery_url: url, saved_at: rec.saved_at, driver: 'link' });
  }

  function linkCodeFromUrl() {
    try {
      var v = new root.URL(root.location.href).searchParams.get(config.linkParam);
      return isLinkCode(v) ? v : null;
    } catch (e) { return null; }
  }

  function b64urlEncode(str) {
    var b = root.btoa(unescape(encodeURIComponent(str)));
    return b.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
  function b64urlDecode(s) {
    s = String(s).replace(/-/g, '+').replace(/_/g, '/');
    while (s.length % 4) s += '=';
    return decodeURIComponent(escape(root.atob(s)));
  }

  /* ---- AppProxyStore -------------------------------------------------------
     All calls go to the merchant's own domain (App Proxy), so first-party,
     cookie-authenticated, CSRF-safe with Shopify's proxy signature on the
     server. Client sends NO secrets. Email (if provided) is sent ONCE to the
     proxy over POST body — never a query string.
     -------------------------------------------------------------------------- */
  function proxyUrl(path) { return config.proxyBase.replace(/\/$/, '') + path; }

  function proxySave(rec) {
    rec = stamp(rec);
    return postJSON(proxyUrl('/designs'), {
      design: sanitizeForServer(rec),
      builder_session_id: sid(),
      // email is passed here only if the caller explicitly attached it via save({email})
      email: rec.__email || undefined
    }).then(function (res) {
      // res: { design_id, recovery_token, saved_at }
      var recovery_url = res.recovery_token ? proxyUrl(config.recoveryPath + res.recovery_token) : null;
      // remember token locally (opaque) so this device can offer "resume"
      try { root.localStorage.setItem(config.localKeyPrefix + 'token.' + res.design_id, res.recovery_token || ''); } catch (e) {}
      return { design_id: res.design_id, recovery_token: res.recovery_token, recovery_url: recovery_url, saved_at: res.saved_at, driver: 'appproxy' };
    });
  }
  function proxyLoad(idOrToken) {
    var isToken = String(idOrToken).length > 20 && String(idOrToken).indexOf('dsgn_') !== 0;
    var url = isToken ? proxyUrl(config.recoveryPath + idOrToken + '.json') : proxyUrl('/designs/' + idOrToken + '.json');
    return getJSON(url).then(function (res) { return res && res.design ? res.design : res; }).catch(function () { return null; });
  }
  function proxyAttachEmail(design_id, email) {
    return postJSON(proxyUrl('/designs/' + design_id + '/email'), { email: email, builder_session_id: sid() })
      .then(function (res) {
        var recovery_url = res.recovery_token ? proxyUrl(config.recoveryPath + res.recovery_token) : (res.recovery_url || null);
        return { ok: true, recovery_url: recovery_url, recovery_token: res.recovery_token };
      });
  }

  /* ---- public interface ---------------------------------------------------- */
  function driver() { return config.driver === 'appproxy' || config.driver === 'link' ? config.driver : 'local'; }

  // opts.marketing: true only when the customer ticked the consent box
  // opts.listId:    Klaviyo list to subscribe them to in that case
  function save(rec, opts) {
    opts = opts || {};
    var email = rec && rec.email;
    if (email) { rec = clone(rec); rec.__email = email; delete rec.email; if (!rec.identity) rec.identity = {}; rec.identity.email_provided = true; }
    var d = driver();
    var p = d === 'appproxy' ? proxySave(rec) : d === 'link' ? linkSave(rec) : localSave(rec);
    return p.then(function (res) {
      // identify BEFORE tracking, so onsite destinations attribute the save to
      // this person rather than an anonymous visitor
      if (email && Analytics && Analytics.identify) {
        Analytics.identify({ email: email, source: 'save', design_id: res.design_id });
      }
      // keep identity's lightweight cache in step
      if (Identity && Identity.patchDesignMeta) {
        Identity.patchDesignMeta({
          jewelry_type: rec.jewelry_type, design_source: rec.design_source,
          element_count: countEls(rec), design_total: rec.design_value, currency: rec.currency,
          saved: true, saved_at: res.saved_at
        });
      }
      if (email && Identity && Identity.link) Identity.link({ email_provided: true, source: 'save', design_id: res.design_id });
      var props = {
        jewelry_type: rec.jewelry_type,
        design_source: rec.design_source,
        element_count: countEls(rec),
        design_total: rec.design_value,
        currency: rec.currency,
        curated_design_id: rec.curated_design_id || undefined,
        recovery_url: res.recovery_url || undefined,
        driver: res.driver,
        elements: savedElementsForEvent(rec)
      };
      // analytics: design_saved (custom event) for shopify / ga4 / meta
      if (Analytics && Analytics.track) Analytics.track('design_saved', props);

      // Klaviyo: sent direct, keyed on the typed email, so the "Design Saved"
      // flow fires even when onsite tracking is blocked. Failure never blocks
      // the save itself; it's reported on res.klaviyo.
      var K = Disegno.KlaviyoClient;
      if (!email || !K || !K.ready()) { res.klaviyo = email ? 'not_configured' : 'no_email'; return res; }
      var kEvent = clone(props);
      kEvent.design_id = res.design_id; kEvent.saved_at = res.saved_at;
      kEvent.builder_session_id = sid();
      var jobs = [K.track(email, 'design_saved', kEvent, { disegno_last_saved_design_url: res.recovery_url || undefined })];
      if (opts.marketing) jobs.push(K.subscribe(email, opts.listId, 'DiSEGNO Save your design'));
      return Promise.all(jobs)
        .then(function () { res.klaviyo = 'sent'; return res; })
        .catch(function (e) { res.klaviyo = 'failed'; try { console.warn('[DiSEGNO] Klaviyo', e); } catch (_) {} return res; });
    });
  }

  function load(idOrToken, opts) {
    opts = opts || {};
    var p = isLinkCode(idOrToken) ? Promise.resolve(decodeLink(idOrToken))
      : driver() === 'appproxy' ? proxyLoad(idOrToken) : localLoad(idOrToken);
    return p.then(function (rec) {
      // `silent` is used for a device-local draft RESUME — not a real
      // "reopened a saved design" moment, so no design_loaded event.
      if (rec && !opts.silent && Analytics && Analytics.track) {
        Analytics.track('design_loaded', {
          jewelry_type: rec.jewelry_type, design_source: rec.design_source,
          element_count: countEls(rec), design_total: rec.design_value, currency: rec.currency,
          load_source: opts.load_source || 'reopen'
        });
      }
      return rec;
    });
  }

  function listForSession() { return driver() === 'appproxy' ? Promise.resolve([]) : localList(); }
  function attachEmail(design_id, email) {
    if (driver() !== 'appproxy') {
      // local / link: no server record to attach to; identify only
      if (Identity && Identity.link) Identity.link({ email_provided: true, source: 'save', design_id: design_id });
      if (Analytics && Analytics.identify) Analytics.identify({ email: email, source: 'save', design_id: design_id });
      return Promise.resolve({ ok: true, recovery_url: null, note: 'local driver: email captured for Klaviyo only, no server record' });
    }
    return proxyAttachEmail(design_id, email);
  }

  /* Resolve an App Proxy recovery token from the URL (?disegno_d= or
     /apps/disegno/d/<t>). Link codes (?design=v1.…) are NOT tokens: the viewer
     restores those inside the builder via linkCodeFromUrl(). */
  function tokenFromUrl() {
    try {
      var u = new root.URL(root.location.href);
      var q = u.searchParams.get('disegno_d') || u.searchParams.get('design');
      if (q && !isLinkCode(q)) return q;
      var m = u.pathname.match(/\/d\/([A-Za-z0-9_\-]{16,})/);
      return m ? m[1] : null;
    } catch (e) { return null; }
  }

  /* ---- utils ---------------------------------------------------------------- */
  function stamp(rec) {
    rec = clone(rec || {});
    rec.design_id = rec.design_id || (Identity && Identity.getDesignId ? Identity.getDesignId() : 'dsgn_' + Date.now());
    rec.builder_session_id = rec.builder_session_id || sid();
    var now = new Date().toISOString();
    rec.saved_at = rec.saved_at || now;
    rec.updated_at = now;
    if (!rec.identity) rec.identity = { identified: Identity && Identity.isIdentified ? Identity.isIdentified() : false };
    return rec;
  }
  function sanitizeForServer(rec) {
    var out = clone(rec);
    delete out.__email;             // sent as a sibling field, not inside `design`
    return out;
  }
  function countEls(rec) {
    if (rec.jewelry_type === 'earrings') return (rec.left_earring || []).length + (rec.right_earring || []).length;
    return (rec.elements || []).length;
  }
  // full element objects come from the builder's design record (elements_full /
  // *_earring_full); older records only carry variant ids, so this may be empty.
  function savedElementsForEvent(rec) {
    var ear = rec.jewelry_type === 'earrings';
    var list = ear
      ? (rec.left_earring_full || []).map(function (e) { return [e, 'left']; })
          .concat((rec.right_earring_full || []).map(function (e) { return [e, 'right']; }))
      : (rec.elements_full || []).map(function (e) { return [e, null]; });
    var pos = { left: 0, right: 0, none: 0 };
    return list.map(function (pair) {
      var e = pair[0], side = pair[1];
      return { variant_id: e.variant_id, sku: e.sku, element_name: e.title || e.name, element_image: e.image,
        position: pos[side || 'none']++, ear_side: side || undefined };
    });
  }
  function sid() { return Identity && Identity.getSessionId ? Identity.getSessionId() : null; }
  function clone(o) { try { return JSON.parse(JSON.stringify(o)); } catch (e) { return o; } }
  function postJSON(url, body) {
    return root.fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body), credentials: 'same-origin' }).then(handle);
  }
  function getJSON(url) { return root.fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then(handle); }
  function handle(r) {
    return r.text().then(function (t) {
      var j; try { j = t ? JSON.parse(t) : {}; } catch (e) { j = {}; }
      if (!r.ok) { var e2 = new Error('SavedDesign HTTP ' + r.status); e2.response = j; throw e2; }
      return j;
    });
  }

  Disegno.SavedDesign = {
    configure: configure,
    driver: driver,
    proxyBase: function () { return config.proxyBase; },
    save: save,
    load: load,
    listForSession: listForSession,
    attachEmail: attachEmail,
    tokenFromUrl: tokenFromUrl,
    linkCodeFromUrl: linkCodeFromUrl,
    encodeLink: encodeLink,
    decodeLink: decodeLink
  };
})(typeof window !== 'undefined' ? window : this);
